<script setup lang="tsx">
import HeaderComponent from '../shared/HeaderComponent.vue'
import RiskTabs from './RiskTabs.vue'
import ThreatsPane from './threat/ThreatsPane.vue'
import ThreatsToolbar from './threat/ThreatsToolbar.vue'
import { computed, onMounted, ref } from 'vue'
import IconButton from '../shared/IconButton.vue'
import { Tabs } from '@/types/risk-management'
import RisksPane from './RisksPane.vue'
import { useI18n } from 'vue-i18n'
import RiskSettingsTab from './matrix/RiskSettingsTab.vue'
import VulnerabilityPane from './vulnerability/VulnerabilityPane.vue'
import VulnerabilityToolbar from './vulnerability/VulnerabilityToolbar.vue'
import { getRiskConfig, riskConfig } from './riskConfig'
import { refreshThreatCallback } from './threat/threatShared'
import { refreshVulnerabilityCallback } from './vulnerability/vulnerabilityShared'
import RiskOverviewInstances from './overview/RiskOverviewInstances.vue'
import { hasPermission, viewing } from '@/services/utils'
import { PermRiskAdd, PermAllRiskWrite, PermOwnRiskWrite } from '@/models/enums-generated'

const { t } = useI18n()
const activeTab = ref<Tabs>(Tabs.Risks)
const tabs = ref<InstanceType<typeof RiskTabs>>()
const risksRef = ref<InstanceType<typeof RisksPane>>()
const settingsRef = ref<InstanceType<typeof RiskSettingsTab>>()
const threatsRef = ref<InstanceType<typeof ThreatsPane>>()
const vulnerabilitiesRef = ref<InstanceType<typeof VulnerabilityPane>>()
const matrixSection = computed(() => settingsRef.value?.matrixSection)
const riskScoreSection = computed(() => settingsRef.value?.riskScoreSection)
const previousTab = ref<Tabs>(Tabs.Risks)

const hasActiveCatalogs = computed(() => riskConfig.value.catalogs.length > 0)
const canEditRisks = computed(() => hasPermission(PermAllRiskWrite, PermOwnRiskWrite))
const canAddRisks = computed(() => hasPermission(PermRiskAdd))

type HeaderButton = {
	id: string
	onPress: () => void
	title: string
	tooltip?: string
	class: string
	disable?: boolean
}
const headerButtons = computed<Record<Tabs, HeaderButton[]>>(() => ({
	[Tabs.Risks]: [
		{
			id: 'export-risks-report',
			onPress: () => risksRef.value?.handleExportPdf(),
			title: t('pdf.export'),
			class: 'bi bi-file-pdf',
		},
		{
			id: 'edit-risks-button',
			onPress: () => risksRef.value?.editRisk(),
			title: t('misc.edit'),
			class: 'bi bi-pencil',
			disable: !canEditRisks.value,
		},
		{
			id: 'add-risk-button',
			onPress: () => risksRef.value?.addRisk(),
			title: t('misc.add'),
			tooltip: !hasActiveCatalogs.value ? t('risk.addingDisabled') : undefined,
			class: 'bi bi-plus-square',
			disable: !hasActiveCatalogs.value || !canAddRisks.value,
		},
	],
	[Tabs.RiskSettings]: [
		{
			id: 'edit-risk-settings-button',
			onPress: () => {
				if (settingsRef.value) settingsRef.value.isEdit = true
			},
			title: t('misc.edit'),
			class: 'bi bi-pencil',
		},
	],
	[Tabs.Threats]: [
		{
			id: 'edit-threat-button',
			onPress: () => threatsRef.value?.editThreat(),
			title: t('misc.edit'),
			class: 'bi bi-pencil',
		},
		{
			id: 'add-threat-button',
			onPress: () => threatsRef.value?.addThreat(),
			title: t('misc.add'),
			tooltip: !hasActiveCatalogs.value ? t('risk.addingDisabled') : undefined,
			class: 'bi bi-plus-square',
			disable: !hasActiveCatalogs.value,
		},
	],
	[Tabs.Vulnerabilities]: [
		{
			id: 'edit-vulnerability-button',
			onPress: () => vulnerabilitiesRef.value?.editVulnerability(),
			title: t('misc.edit'),
			class: 'bi bi-pencil',
		},
		{
			id: 'add-vulnerability-button',
			onPress: () => vulnerabilitiesRef.value?.addVulnerability(),
			title: t('misc.add'),
			tooltip: !hasActiveCatalogs.value ? t('risk.addingDisabled') : undefined,
			class: 'bi bi-plus-square',
			disable: !hasActiveCatalogs.value,
		},
	],
}))

const onSettingsChange = () => {
	risksRef.value?.fetchRisks()
	refreshThreatCallback.value?.()
	refreshVulnerabilityCallback.value?.()
}

const currentTabButtons = computed(() => headerButtons.value[activeTab.value])

function handleSwitchToMatrix() {
	previousTab.value = activeTab.value
	activeTab.value = Tabs.RiskSettings
}

function handleRestoreTab() {
	activeTab.value = previousTab.value
}

onMounted(async () => {
	if (viewing.value > 0) {
		riskConfig.value = await getRiskConfig()
	}
})
</script>

<template>
	<HeaderComponent :title="$t('risk.riskManagement')">
		<template #right-side>
			<div class="d-flex align-items-center gap-2">
				<div v-for="btn in currentTabButtons" :key="btn.id" v-one-tooltip="btn.tooltip || btn.title" class="position-relative">
					<IconButton :data-test="btn.id" @click="btn.onPress" :title="btn.title" :class="btn.class" :disabled="btn.disable" />
				</div>
			</div>
		</template>
	</HeaderComponent>
	<template v-if="viewing > 0">
		<RiskTabs v-model="activeTab" ref="tabs">
			<template #risks>
				<RisksPane
					ref="risksRef"
					:matrix-section="matrixSection"
					:risk-score-section="riskScoreSection"
					@switch-to-matrix-tab="handleSwitchToMatrix"
					@restore-tab="handleRestoreTab"
				></RisksPane>
			</template>
			<template #riskSettings>
				<RiskSettingsTab v-if="activeTab === Tabs.RiskSettings" ref="settingsRef" @changed="onSettingsChange"></RiskSettingsTab>
			</template>
			<template #threats>
				<ThreatsPane
					ref="threatsRef"
					@go-to-risks="
						(names, treatments) => {
							activeTab = Tabs.Risks
							risksRef?.filterRisks(names, treatments)
						}
					"
				/>

				<div v-if="threatsRef?.isEdit" class="pb-5"></div>
				<ThreatsToolbar
					v-if="threatsRef?.isEdit"
					:num-changes="threatsRef?.numChanges"
					:can-save="threatsRef?.canSave"
					:is-saving="threatsRef?.isSaving"
					@save="threatsRef?.save()"
					@reset="threatsRef?.reset()"
				/>
			</template>
			<template #vulnerabilities>
				<VulnerabilityPane
					ref="vulnerabilitiesRef"
					@go-to-risks="
						(names, treatments) => {
							activeTab = Tabs.Risks
							risksRef?.filterRisks(names, treatments)
						}
					"
				></VulnerabilityPane>
				<div v-if="vulnerabilitiesRef?.isEdit" class="pb-5"></div>
				<VulnerabilityToolbar
					v-if="vulnerabilitiesRef?.isEdit"
					:num-changes="vulnerabilitiesRef?.numChanges"
					:can-save="vulnerabilitiesRef?.canSave"
					:is-saving="vulnerabilitiesRef?.isSaving"
					@save="vulnerabilitiesRef?.save()"
					@reset="vulnerabilitiesRef?.reset()"
				/>
			</template>
		</RiskTabs>
	</template>
	<template v-else> <RiskOverviewInstances :is-dashboard="false"></RiskOverviewInstances></template>
</template>

<style lang="sass" scoped></style>
